/**
 * Titles and descriptions for the public league pages.
 *
 * These mirror the server-rendered copy in `api/app/services/link_preview.rb`,
 * which is what social crawlers actually see. Keep the two in step — the Ruby
 * side is the one that shows up in a Slack unfurl.
 */

/** Appended to league descriptions, matching LinkPreview::LEAGUE_ATTRIBUTION. */
const ATTRIBUTION = "Another league on Podium.";

interface SectionCopy {
  /** Appended to the league name as "<League> — <label>". */
  label: string;
  /** Receives the league name. */
  describe: (leagueName: string) => string;
}

const SECTIONS: Record<string, SectionCopy> = {
  standings: {
    label: "Standings",
    describe: (league) => `Current standings and points for ${league}.`,
  },
  schedule: {
    label: "Schedule",
    describe: (league) => `Upcoming games and recent results for ${league}.`,
  },
  stats: {
    label: "Stats",
    describe: (league) =>
      `Scoring leaders and goaltending stats for ${league}.`,
  },
};

export interface LeaguePageMetadata {
  title: string;
  description: string;
}

/**
 * Builds the metadata for a public league page.
 *
 * @param pathname - Current location pathname, e.g. "/leagues/NSW/standings".
 * @param leagueName - Resolved league name, if it has loaded yet.
 * @param detail - Overrides the section label for record pages, e.g. a team name.
 */
export function leaguePageMetadata(
  pathname: string,
  leagueName: string | null | undefined,
  detail?: string | null,
): LeaguePageMetadata | null {
  if (!leagueName) return null;

  if (detail) {
    return {
      title: `${leagueName} — ${detail}`,
      description: `${detail} in ${leagueName}. ${ATTRIBUTION}`,
    };
  }

  const section = SECTIONS[sectionFrom(pathname)];

  if (!section) {
    return {
      title: leagueName,
      description: `Live standings, schedule and stats for ${leagueName}. ${ATTRIBUTION}`,
    };
  }

  return {
    title: `${leagueName} — ${section.label}`,
    description: `${section.describe(leagueName)} ${ATTRIBUTION}`,
  };
}

/**
 * The section segment that follows "/leagues/:abbr", or "" for the league home.
 */
function sectionFrom(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);

  // ["leagues", ":abbr", <section>, ...]
  return segments[0] === "leagues" ? segments[2] ?? "" : segments[0] ?? "";
}
